import { isGithubBranchSwitch } from '~/lib/client-github';
import type { Message } from '~/types/chat';

export function filterMessages(messages: Array<Readonly<Message>>, maxPairs: number) {
  const lastMessage = messages.at(-1);

  /**
   * The Anthropic API doesn't support resuming interrupted reasoning sessions - it expects a stable connection
   * throughout the entire reasoning process. In these cases, we must show an error to the user and ask them to
   * retry their request.
   */
  if (lastMessage && lastMessage.role === 'assistant' && !lastMessage.content) {
    throw new Error('Something went wrong. Please try again.');
  }

  const lastBranchSwitchIndex = messages.findLastIndex(isGithubBranchSwitch);

  const validMessagePairs: Array<{ userIndex: number; assistantIndex: number }> = [];

  for (let i = lastBranchSwitchIndex + 1; i < messages.length - 1; i++) {
    const userMessage = messages[i];
    const assistantMessage = messages[i + 1];

    // check if this is a valid user/assistant pair
    if (userMessage.role !== 'user' || assistantMessage.role !== 'assistant') {
      continue;
    }

    // skip if either message is ignored
    if (userMessage.ignore || assistantMessage.ignore) {
      continue;
    }

    // skip if user message is hidden
    if (userMessage.hidden) {
      continue;
    }

    // skip problem messages that haven't been resolved
    if (userMessage.type === 'problems' && !userMessage.resolved) {
      continue;
    }

    // skip messages without content
    if (!userMessage.content || !assistantMessage.content) {
      continue;
    }

    validMessagePairs.push({ userIndex: i, assistantIndex: i + 1 });
  }

  const pairsToInclude = validMessagePairs.slice(-maxPairs);

  const result: Array<Readonly<Message>> = [];

  // add messages from pairs
  for (const pair of pairsToInclude) {
    result.push(messages[pair.userIndex], messages[pair.assistantIndex]);
  }

  if (
    lastMessage &&
    lastMessage.role === 'user' &&
    !lastMessage.ignore &&
    !lastMessage.hidden &&
    lastMessage.content &&
    (lastMessage.type === 'chat' || lastMessage.type === 'problems') &&
    !isGithubBranchSwitch(lastMessage)
  ) {
    result.push(lastMessage);
  }

  return result;
}
